// ============================================================================
// Hooks TanStack Query — ADMINISTRATION : grants CE (comite executif)
// ============================================================================
// Bundle GET /api/admin/executive-grants + mutations POST/DELETE. Garde serveur
// P-ADM-2 (admin seul). Invalidation ['gest','admin'] apres ecriture.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiForbiddenError, ApiServerError } from '@gs/api-client';
import { ADMIN_ROOT_KEY } from './useAdminAudit';

export interface ExecutiveGrantRow {
  id: string;
  granteeEmail: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface AdminExecutiveGrantsResponse {
  viewer: { role: string; email: string };
  grants: ExecutiveGrantRow[];
}

const grantsKey = [...ADMIN_ROOT_KEY, 'executive-grants'] as const;

export function useAdminExecutiveGrants(enabled: boolean) {
  return useQuery({
    queryKey: grantsKey,
    queryFn: () => api.get<AdminExecutiveGrantsResponse>('/api/admin/executive-grants'),
    enabled,
    retry: false,
  });
}

export function useUpsertExecutiveGrant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (email: string) =>
      api.post<{ id: string }>('/api/admin/executive-grants', { email }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ADMIN_ROOT_KEY }),
  });
}

export function useRevokeExecutiveGrant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<{ ok: boolean }>(`/api/admin/executive-grants/${id}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ADMIN_ROOT_KEY }),
  });
}

export function adminExecutiveGrantErrorCode(err: unknown): string {
  if (err instanceof ApiForbiddenError) return 'forbidden';
  if (err instanceof ApiServerError) {
    const body = err.originalBody;
    if (typeof body === 'object' && body && 'code' in body) {
      return String((body as { code: string }).code);
    }
  }
  return 'unknown';
}
